/**
 * Chat Input Component
 * Clean, functional chat input for Study Mode
 */

import {useState, KeyboardEvent, useRef, useEffect} from 'react';
import {cn} from '../../utils/cn';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'How can I help you today?',
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSend(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="w-full">
      {/* Main Input Container */}
      <div className="relative bg-white dark:bg-[#252526] rounded-lg border border-gray-200/60 dark:border-gray-700/60 shadow-sm transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 focus-within:shadow-md focus-within:border-orange-300 dark:focus-within:border-orange-700 focus-within:ring-2 focus-within:ring-orange-500/10 dark:focus-within:ring-orange-500/20">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            'w-full px-4 pt-4 pb-20 rounded-lg resize-none',
            'bg-transparent',
            'text-textPrimary dark:text-[#CCCCCC]',
            'placeholder-gray-400 dark:placeholder-[#6A6A6A]',
            'focus:outline-none',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors duration-200'
          )}
          style={{
            minHeight: '80px',
            maxHeight: '400px',
          }}
        />

        {/* Bottom Bar */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end px-4 py-3 border-t border-gray-100/60 dark:border-gray-700/60 rounded-b-lg">
          {/* Send Button */}
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || !message.trim()}
            className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200',
              'bg-orange-600 dark:bg-orange-500 text-white',
              'hover:bg-orange-700 dark:hover:bg-orange-600',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-orange-600 dark:disabled:hover:bg-orange-500',
              'shadow-sm hover:shadow-md hover:shadow-orange-600/25 dark:hover:shadow-orange-500/25',
              'transform active:scale-95',
              !disabled && message.trim() && 'hover:scale-105'
            )}
            title="Send message"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
